/**
 * Session Token Service
 *
 * Single chokepoint for the active session JWT carried in
 * `Authorization: Bearer <jwt>`. The token rotates: `accountJWT`
 * immediately after signIn → `orgJWT` of the auto-selected first org →
 * `orgJWT` of whatever org the user switches to.
 *
 * `orgJWT` is a permission superset of `accountJWT` (server PR #347), so
 * a single key in localStorage is sufficient — we never keep both around.
 *
 * See docs/specs/auth-single-token-migration.md.
 */
import { restartWebSocketConnection } from '@api';

import {
  StorageKey,
  getItem,
  removeItem,
  setItem,
} from '@services/storageService';

/**
 * Get the active session token.
 *
 * @returns The active session JWT or null if not signed in.
 */
export const getActiveSessionToken = (): string | null => {
  return getItem(StorageKey.TOKEN);
};

const SESSION_TOKEN_CHANGED = 'marble-session-token-changed';

const tokenExpiresAt = (token: string | null): number => {
  try {
    const payload = token?.split('.')[1];
    if (!payload) return 0;
    const { exp } = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    );
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : 0;
  } catch {
    return 0;
  }
};

// Client scheduling only; the server still verifies the signature and claims.
export const getValidSessionToken = (): string | null => {
  const token = getActiveSessionToken();
  return tokenExpiresAt(token) > Date.now() ? token : null;
};

export const subscribeSessionToken = (listener: () => void): (() => void) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const changed = (): void => {
    clearTimeout(timer);
    listener();
    const remaining = tokenExpiresAt(getActiveSessionToken()) - Date.now();
    if (remaining > 0)
      timer = setTimeout(changed, Math.min(remaining, 2_147_483_647));
  };
  window.addEventListener(SESSION_TOKEN_CHANGED, changed);
  window.addEventListener('storage', changed);
  window.addEventListener('focus', changed);
  changed();
  return () => {
    clearTimeout(timer);
    window.removeEventListener(SESSION_TOKEN_CHANGED, changed);
    window.removeEventListener('storage', changed);
    window.removeEventListener('focus', changed);
  };
};

/**
 * Set the active session token.
 *
 * Used directly when the active token rotates without a need to reconnect
 * the WS subscription socket — e.g., right after signIn before any WS
 * connection is established.
 *
 * @param jwt - The session JWT to store.
 */
export const setActiveSessionToken = (jwt: string): void => {
  setItem(StorageKey.TOKEN, jwt);
  window.dispatchEvent(new Event(SESSION_TOKEN_CHANGED));
};

/**
 * Remove the active session token.
 */
export const removeActiveSessionToken = (): void => {
  removeItem(StorageKey.TOKEN);
  window.dispatchEvent(new Event(SESSION_TOKEN_CHANGED));
};

/**
 * Switch the active session to an org-scoped JWT and reconnect the WS
 * socket so subscriptions pick up the new auth context.
 *
 * Use this at every switch-org / create-org call site instead of writing
 * to localStorage directly.
 *
 * @param orgToken - The org-scoped JWT to activate.
 */
export const applyActiveOrgToken = (orgToken: string): void => {
  setActiveSessionToken(orgToken);
  restartWebSocketConnection();
};
